/**
 * Project-level crew assignment (A2 of the FMS-feedback build, D-3).
 *
 * Crew are assigned to the PROJECT directly — no task needed. Each assigned
 * crew member holds one project-anchored assignment block per committed
 * schedule day (from `approved_schedule`), materialized by the API; the
 * supervisor only ever expresses "who's on this project, and who leads".
 * Mirrors the task-level picker (`TaskAssignmentsSetSchema`) on purpose so the
 * same AssignCrewDialog drives both.
 */
import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { AssignmentAnchorKindSchema } from './enums.js';
import { CrewRoleSchema } from './users.js';
import { TaskAssignmentInputSchema } from './tasks.js';
/**
 * A soft double-booking warning (D-11 warn-not-block). A crew member being
 * assigned already holds a timed block that overlaps — but the overlap touches
 * a PICKUP (a quick warehouse errand Leo routinely stacks with an install), so
 * it's surfaced for the supervisor to eyeball, never rejected. `label` names the
 * other commitment (the pickup's title, or the project/task it clashes with).
 */
export const BlockWarningSchema = z.object({
    crew_member_id: UuidSchema,
    name: z.string(),
    anchor_kind: AssignmentAnchorKindSchema,
    label: z.string(),
});
/** One assignment block a crew member holds on the project (one per committed day). */
export const ProjectAssignmentBlockSchema = z.object({
    id: UuidSchema,
    scheduled_start: IsoDateTimeSchema.nullable(),
    scheduled_end: IsoDateTimeSchema.nullable(),
});
/** A crew member assigned to the project, with the blocks they hold. */
export const ProjectAssignmentSchema = z.object({
    crew_member_id: UuidSchema,
    user_id: UuidSchema,
    display_name: z.string(),
    avatar_url: z.string().nullable(),
    crew_role: CrewRoleSchema,
    is_lead: z.boolean(),
    blocks: z.array(ProjectAssignmentBlockSchema),
});
export const ProjectAssignmentsSchema = z.object({
    project_id: UuidSchema,
    assignments: z.array(ProjectAssignmentSchema),
    /** Soft pickup-overlap warnings from the last set/reassign (D-11). */
    warnings: z.array(BlockWarningSchema).default([]),
});
/**
 * Set the full crew set on a project at once — the API reconciles adds /
 * removals / lead against what's there and (re)materializes one block per crew
 * member per committed day. Empty array unassigns everyone. Same shape as the
 * task picker's payload.
 */
export const ProjectAssignmentsSetSchema = z.object({
    assignments: z.array(TaskAssignmentInputSchema).max(20),
    /** Supervisor-confirmed reassign: resolve a time clash by MOVING the crew
     *  member off the overlapping block(s) elsewhere onto this project, instead of
     *  rejecting the double-booking. */
    reassign_conflicts: z.boolean().optional(),
});
/**
 * Move one crew member on/off a project (dispatch-board drag). Same semantics as
 * the task version: from+to moves, from-only removes, to-only adds.
 */
export const ProjectReassignSchema = z
    .object({
    from_crew_member_id: UuidSchema.nullable().optional(),
    to_crew_member_id: UuidSchema.nullable().optional(),
})
    .refine((v) => v.from_crew_member_id || v.to_crew_member_id, {
    message: 'At least one of from_crew_member_id or to_crew_member_id is required',
});
//# sourceMappingURL=project-assignments.js.map